import { create } from 'zustand';

/**
 * YouTube 플레이어 상태 스토어
 *
 * videoId 기반 스코프 관리로 여러 영상을 독립적으로 제어합니다.
 * - VideoInfoBlock: 재생 시간 업데이트, seek 처리
 * - TranscriptBlock: 현재 시간 구독, seek 요청
 */

// ============================================================================
// TYPES
// ============================================================================

interface PlayerState {
  /** 현재 재생 시간 (초) */
  currentTime: number;
  /** 재생 중 여부 */
  isPlaying: boolean;
  /** 영상 총 길이 (초) */
  duration: number;
}

interface SeekRequest {
  /** 이동할 위치 (초) */
  seconds: number;
  /** 요청 고유 ID (중복 실행 방지) */
  requestId: number;
}

interface YoutubePlayerState {
  /** videoId별 플레이어 상태 */
  players: Map<string, PlayerState>;
  /** videoId별 seek 요청 */
  seekRequests: Map<string, SeekRequest>;
  /** videoId별 자동 스크롤 활성화 여부 */
  autoScrollEnabled: Map<string, boolean>;
  /** videoId별 비디오 요소 ref (스크롤용) */
  videoRefs: Map<string, HTMLElement>;
}

interface YoutubePlayerActions {
  /** 현재 재생 시간 업데이트 (VideoInfoBlock에서 500ms 간격 호출) */
  updateCurrentTime: (videoId: string, time: number) => void;

  /** 재생 상태 설정 (VideoInfoBlock에서 호출) */
  setPlayState: (videoId: string, isPlaying: boolean, duration: number) => void;

  /** seek 요청 (TranscriptBlock에서 타임스탬프 클릭 시 호출) */
  requestSeek: (videoId: string, seconds: number) => void;

  /** seek 요청 처리 완료 (VideoInfoBlock에서 seekTo 실행 후 호출) */
  clearSeekRequest: (videoId: string, requestId: number) => void;

  /** 자동 스크롤 토글 */
  toggleAutoScroll: (videoId: string) => void;

  /** 플레이어 정리 (컴포넌트 언마운트 시 호출) */
  removePlayer: (videoId: string) => void;

  /** 비디오 요소 ref 등록 (스크롤용) */
  registerVideoRef: (videoId: string, element: HTMLElement) => void;

  /** 비디오 요소 ref 해제 */
  unregisterVideoRef: (videoId: string) => void;
}

type YoutubePlayerStore = YoutubePlayerState & YoutubePlayerActions;

// ============================================================================
// STORE
// ============================================================================

export const useYoutubePlayerStore = create<YoutubePlayerStore>()((set, get) => ({
  // State
  players: new Map(),
  seekRequests: new Map(),
  autoScrollEnabled: new Map(),
  videoRefs: new Map(),

  // Actions
  updateCurrentTime: (videoId, time) =>
    set((state) => {
      const newPlayers = new Map(state.players);
      const existing = newPlayers.get(videoId) || {
        currentTime: 0,
        isPlaying: false,
        duration: 0,
      };
      newPlayers.set(videoId, { ...existing, currentTime: time });
      return { players: newPlayers };
    }),

  setPlayState: (videoId, isPlaying, duration) =>
    set((state) => {
      const newPlayers = new Map(state.players);
      const existing = newPlayers.get(videoId) || {
        currentTime: 0,
        isPlaying: false,
        duration: 0,
      };
      newPlayers.set(videoId, { ...existing, isPlaying, duration });
      return { players: newPlayers };
    }),

  requestSeek: (videoId, seconds) => {
    // 비디오 요소로 스크롤 (상태 업데이트 전에 수행)
    const videoElement = get().videoRefs.get(videoId);
    if (videoElement) {
      videoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // seek 요청 상태 업데이트
    set((state) => {
      const newSeekRequests = new Map(state.seekRequests);
      newSeekRequests.set(videoId, { seconds, requestId: Date.now() });
      return { seekRequests: newSeekRequests };
    });
  },

  clearSeekRequest: (videoId, requestId) =>
    set((state) => {
      const current = state.seekRequests.get(videoId);
      if (current?.requestId === requestId) {
        const newSeekRequests = new Map(state.seekRequests);
        newSeekRequests.delete(videoId);
        return { seekRequests: newSeekRequests };
      }
      return state;
    }),

  toggleAutoScroll: (videoId) =>
    set((state) => {
      const newAutoScroll = new Map(state.autoScrollEnabled);
      const current = newAutoScroll.get(videoId) ?? true;
      newAutoScroll.set(videoId, !current);
      return { autoScrollEnabled: newAutoScroll };
    }),

  removePlayer: (videoId) =>
    set((state) => {
      const newPlayers = new Map(state.players);
      const newSeekRequests = new Map(state.seekRequests);
      const newAutoScroll = new Map(state.autoScrollEnabled);
      const newVideoRefs = new Map(state.videoRefs);

      newPlayers.delete(videoId);
      newSeekRequests.delete(videoId);
      newAutoScroll.delete(videoId);
      newVideoRefs.delete(videoId);

      return {
        players: newPlayers,
        seekRequests: newSeekRequests,
        autoScrollEnabled: newAutoScroll,
        videoRefs: newVideoRefs,
      };
    }),

  registerVideoRef: (videoId, element) =>
    set((state) => {
      const newVideoRefs = new Map(state.videoRefs);
      newVideoRefs.set(videoId, element);
      return { videoRefs: newVideoRefs };
    }),

  unregisterVideoRef: (videoId) =>
    set((state) => {
      const newVideoRefs = new Map(state.videoRefs);
      newVideoRefs.delete(videoId);
      return { videoRefs: newVideoRefs };
    }),
}));

// ============================================================================
// SELECTOR HOOKS (성능 최적화를 위한 선택적 구독)
// ============================================================================

/**
 * 현재 재생 시간 구독
 *
 * @param videoId 영상 ID (undefined면 0 반환)
 * @returns 현재 재생 시간 (초)
 */
export function useCurrentTime(videoId: string | undefined): number {
  return useYoutubePlayerStore((state) =>
    videoId ? (state.players.get(videoId)?.currentTime ?? 0) : 0,
  );
}

/**
 * seek 요청 구독
 *
 * @param videoId 영상 ID
 * @returns seek 요청 또는 undefined
 */
export function useSeekRequest(videoId: string | undefined): SeekRequest | undefined {
  return useYoutubePlayerStore((state) => (videoId ? state.seekRequests.get(videoId) : undefined));
}

/**
 * 자동 스크롤 활성화 여부 구독
 *
 * @param videoId 영상 ID (undefined면 true 반환)
 * @returns 자동 스크롤 활성화 여부 (기본값 true)
 */
export function useAutoScroll(videoId: string | undefined): boolean {
  return useYoutubePlayerStore((state) =>
    videoId ? (state.autoScrollEnabled.get(videoId) ?? true) : true,
  );
}

/**
 * 재생 중 여부 구독
 *
 * @param videoId 영상 ID
 * @returns 재생 중 여부
 */
export function useIsPlaying(videoId: string | undefined): boolean {
  return useYoutubePlayerStore((state) =>
    videoId ? (state.players.get(videoId)?.isPlaying ?? false) : false,
  );
}
